import AppLink from "components/common/AppLink"
import If from "components/common/If"
import Loading from "components/common/loading/Loading"
import NoData from "components/common/NoData"
import { isVisible } from "utilities/datalist/datalist-utilities";
import { DatalistOnClickType, InternalDatalistTablePropsType } from "types/datalist-types"
import { resolveItemViewValue } from "utilities/datalist/datalist-sort-utilities"
import DatalistTableHeader from "./header/DatalistTableHeader"
import DatalistStickyHeader from "./header/DatalistStickyHeader";
import DatalistTableFooter from "./header/DatalistTableFooter";
import DatalistStickyFooter from "./header/DatalistStickyFooter";
import DatalistRowActions from "../DatalistRowActions";
import useAppContext from "utilities/hooks/useAppContext";

type DatalistTableBodyColumnPropsType<TItemType, TFilterType, TSortType> = InternalDatalistTablePropsType<TItemType, TFilterType, TSortType> & {
    item: TItemType;
    index: number;
    onDelete: () => void;
}

const DatalistTableBodyColumn = <TItemType, TFilterType, TSortType> (props: DatalistTableBodyColumnPropsType<TItemType, TFilterType, TSortType>) : React.ReactNode => {
    const context = useAppContext();
    const item = props.item;
    const index = props.index;
    const rowActions = typeof props.render === 'function' ? undefined : props.render?.actions;
    const row = props.columns.filter(c => isVisible(c)).map(c => {
        let content = c.resolve ? c.resolve(item, index, context) : resolveItemViewValue(c.name, item, c.type);

        let className = (c.className + ' p-2').trim();
        className = typeof c.columnClassName === 'string' ? className + ' ' + c.columnClassName : className;
        className = typeof c.columnClassName === 'function' ? className + ' ' + c.columnClassName(props.item, props.index) : className;

        let timer: any = null;
        let onItemClick = c.onItemClick === undefined ? props.onItemClick : c.onItemClick as DatalistOnClickType<TItemType>;
        if(typeof onItemClick === 'boolean' && onItemClick) {
            onItemClick = undefined;
        }

        if(onItemClick?.link) {
            content = <AppLink to={onItemClick.link.url} params={onItemClick.link.params && onItemClick.link.params(item)} className="block p-2">
                {content}
            </AppLink>;
            className = (c.className + ' p-0').trim();
        }

        const onClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
            if(!onItemClick?.onClick) return;

            if(onItemClick?.type !== 'doubleClick') {
                onItemClick!.onClick!(item, e)
            }

            if(onItemClick?.type === 'doubleClick') {
                clearTimeout(timer);
                if(e.detail === 1) {
                    timer = setTimeout(() => {
                    }, 200);
                }
                else if(e.detail === 2) {
                    onItemClick!.onClick!(item, e)
                }
            }
        }

        let tdProps = {};
        if(onItemClick) {
            switch(onItemClick.type) {
                case 'click':
                    case 'doubleClick':
                    tdProps = {
                        onClick: onClick
                    };
                    break;
                case 'mouseUp':
                    tdProps = {
                        onMouseUp: onItemClick.onClick
                    }
                    break;
                case 'mouseDown':
                    tdProps = {
                        onMouseDown: onItemClick.onClick
                    }
                    break;
            }
        }

        return <td 
            className={`${onItemClick ? 'cursor-pointer' : ''} ${className}`.trim()}
            {...tdProps}
            key={`datalist-table-row-${props.itemId(item)}-${c.name}`}
        >
            {content}
        </td>
    });

    if(rowActions) {
        const actions = typeof rowActions === 'function' ? rowActions(item) : rowActions;
        if(actions) {
            const action = <td key={`datalist-table-row-${props.itemId(item)}-${props.name}-actions`}>
                <DatalistRowActions
                    {...actions}
                    itemId={props.itemId(item)}
                    item={item}
                    view='table' name={props.name}
                    onDelete={props.onDelete}
                />
            </td>
            row.push(action);
        }
    }

    return <>{row}</>;
}

const DatalistTableBodyRow = <TItemType, TFilterType, TSortType> (props: InternalDatalistTablePropsType<TItemType, TFilterType, TSortType>) => {
    const rowTheme = typeof props.render === 'function' ? undefined : props.render?.theme;
    const rowConfig = typeof props.render === 'function' ? undefined : props.render?.row;
    if(props.loading) {
        return <tr key="datalist-table-row-loading">
                <td colSpan={props.columns.length} className="text-center">
                    <Loading />
                </td>
            </tr>;
    }

    if(!props.items?.length) {
        return <tr key="datalist-table-row-nodata">
                <td colSpan={props.columns.length} className="text-center">
                    <NoData />
                </td>
            </tr>;
    }

    const rows = props.items.map((i, index) => {
        let className = 'hover:bg-neutral-200 dark:hover:bg-neutral-700 bg-white dark:bg-black';
        if((!rowTheme || rowTheme === 'chess') && index % 2 === 0) className = 'bg-neutral-50 dark:bg-neutral-900 hover:bg-neutral-200 dark:hover:bg-neutral-700';

        if(rowConfig?.className) {
            let rowClassName: string|undefined = undefined;
            if(typeof rowConfig.className === 'string') {
                rowClassName = rowConfig.className;
            }
            else if(typeof rowConfig.className === 'function') {
                rowClassName = rowConfig.className(i, index, className);
            }

            if(rowClassName !== undefined) {
                className = rowClassName;
            }
        }

        return (
            <tr 
                className={className.trim()}
                key={`datalist-table-row-${props.itemId(i)}`}
            >
                <DatalistTableBodyColumn item={i} index={index} {...props} />
            </tr>
        );
    });

    return <>
        {rows}
    </>;
}

const DatalistTable = <TItemType, TFilterType, TSortType> (props: InternalDatalistTablePropsType<TItemType, TFilterType, TSortType>) => {
    const rowFooter = typeof props.render === 'function' ? undefined : props.render?.footer;
    return (
        <>
            <table className="datalist">
                <DatalistTableHeader {...props} />
                <tbody className="datalist">
                    <DatalistTableBodyRow {...props} />
                </tbody>
                <If condition={rowFooter?.table}>
                    <DatalistTableFooter name={props.name} config={rowFooter?.table} />
                </If>
            </table>
            <DatalistStickyHeader {...props} />
        <DatalistStickyFooter name={props.name} config={rowFooter?.table} />
        </>
    );
}

export default DatalistTable;
